import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

/**
 * Whether the artifacts index card is on the pane, shared between the card and
 * the chat header's toggle.
 *
 * It needs its own context because the two ends sit on opposite sides of a tree
 * boundary. The header is `ResourcePageShell`'s `actions` element, rendered in
 * the shell's bar *above* `ChatWithArtifactPanel` — so it is outside
 * `ArtifactPanelProvider` and cannot read the panel at all. Rather than hoist
 * the panel's provider over the whole page for one boolean, this provider goes
 * around both and carries only what the two ends actually share.
 *
 * The dependency runs both ways, which is why {@link reportCanShow} exists. The
 * header decides whether to draw a control, but only the card knows whether
 * there is anything to draw it for — that answer is "the chat has artifacts and
 * the Canvas is away", and both halves live inside the panel.
 */
export type ArtifactsIndexVisibilityValue = {
  /** Whether the card is showing, when {@link canShow} allows it at all. */
  isVisible: boolean;
  /**
   * Whether the card has anything to show right now — published by the card
   * itself via {@link reportCanShow}. False with no artifacts, and false while
   * the Canvas is on screen, where the artifact's own bar already carries the
   * switcher.
   */
  canShow: boolean;
  /** Show the card if hidden, hide it if showing. */
  toggle: () => void;
  /** Card-side: publish whether the card could be on the pane. */
  reportCanShow: (canShow: boolean) => void;
};

const ArtifactsIndexVisibilityContext =
  createContext<ArtifactsIndexVisibilityValue | null>(null);

/**
 * `null` outside a provider — every surface that is not the Coco chat page.
 * Both ends treat that as "no toggle here": the card shows on its own terms and
 * the header draws nothing.
 */
export const useArtifactsIndexVisibility =
  (): ArtifactsIndexVisibilityValue | null =>
    useContext(ArtifactsIndexVisibilityContext);

export const ArtifactsIndexVisibilityProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // Visible by default: the card is the way back into a Canvas that was put
  // away, so a chat that produces its first artifact has to surface it without
  // the user having found this toggle first. Hiding is a choice, and it lasts
  // until the same toggle undoes it — including across the Canvas opening and
  // closing again, which is what makes it read as dismissal rather than a fold.
  const [isVisible, setIsVisible] = useState(true);
  const [canShow, setCanShow] = useState(false);

  const toggle = useCallback(() => {
    setIsVisible((previous) => !previous);
  }, []);

  const value = useMemo(
    () => ({ isVisible, canShow, toggle, reportCanShow: setCanShow }),
    [isVisible, canShow, toggle]
  );

  return (
    <ArtifactsIndexVisibilityContext.Provider value={value}>
      {children}
    </ArtifactsIndexVisibilityContext.Provider>
  );
};
